import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import EmptyRouterView from '@/components/EmptyRouterView/EmptyRouterView.vue';
import TokenService from '@/services/token.service';
import store from '@/store';
import { SUPPORTED_SU } from '@/config/app.config';
import LocalStoreService from '@/utils/local.store';
import { getScreeningUnitNameFromID } from '@/config/app.config';
import moment from 'moment';

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: '/:screening/at-home',
      component: EmptyRouterView, // must pass a component here
      children: [
        {
          path: '',
          name: 'atHome',
          redirect: { name: 'atHomeIdentification' },
          meta: {
            public: true,  // Allow access to even if not logged in
            onlyWhenLoggedOut: false,
            atHome: true,
          },
        },
        {
          path: 'identification',
          name: 'atHomeIdentification',
          component: () => import('../views/AtHome/ParticipantIdentification/ParticipantIdentification.vue'),
          meta: {
            public: true,  // Allow access to even if not logged in
            onlyWhenLoggedOut: true,
            atHome: true,
          },
        },
        {
          path: 'identity',
          name: 'atHomeIdentity',
          component: () => import('../views/AtHome/QuestIdentity/QuestIdentity.vue'),
          meta: {
            atHome: true,
          },
        },
        {
          path: 'address',
          name: 'atHomeAddress',
          component: () => import('../views/AtHome/QuestAddress/QuestAddress.vue'),
          meta: {
            atHome: true,
          },
        },
        {
          path: 'insurance',
          name: 'atHomeInsurance',
          component: () => import('../views/AtHome/QuestInsurance/QuestInsurance.vue'),
          meta: {
            atHome: true,
          },
        },
        {
          path: 'doctor',
          name: 'atHomeDoctor',
          component: () => import('../views/AtHome/QuestDoctor/QuestDoctor.vue'),
          meta: {
            atHome: true,
          },
        },
        {
          path: 'questions',
          name: 'atHomeQuestions',
          component: () => import('../views/AtHome/QuestQuestions/QuestQuestions.vue'),
          meta: {
            atHome: true,
          },
        },
        {
          path: 'recap',
          name: 'atHomeRecap',
          component: () => import('../views/AtHome/QuestRecap/QuestRecap.vue'),
          meta: {
            atHome: true,
          },
        },
        {
          path: 'confirmation',
          name: 'atHomeConfirmation',
          component: () => import('../views/AtHome/Confirmation/Confirmation.vue'),
          meta: {
            atHome: true,
          },
        },
      ],
  },
  {
    path: '/:screening',
      component: EmptyRouterView, // must pass a component here
      children: [
        {
          path: '',
          name: 'home',
          redirect: 'login',
          meta: {
            public: true,  // Allow access to even if not logged in
            onlyWhenLoggedOut: false,
          },
        },
        {
          path: 'login',
          name: 'login',
          component: () => import('../views/UserAuthentication/UserAuthentication.vue'),
          meta: {
            public: true,  // Allow access to even if not logged in
            onlyWhenLoggedOut: true,
          },
        },
        {
          path: 'participant',
          name: 'participant',
          component: () => import('../views/ParticipantIdentification/ParticipantIdentification.vue'),
        },
        {
          path: ':partId/overview',
          name: 'overview',
          component: () => import('../views/QuestOverview/QuestOverview.vue'),
          meta: {
            onlyWhenDossierOpened: true,
          },
        },
        {
          path: ':partId/identity',
          name: 'identity',
          component: () => import('../views/QuestIdentity/QuestIdentity.vue'),
          meta: {
            onlyWhenDossierOpened: true,
          },
        },
        {
          path: ':partId/consent',
          name: 'consent',
          component: () => import('../views/QuestConsent/QuestConsent.vue'),
          meta: {
            onlyWhenDossierOpened: true,
          },
          beforeEnter: (to, from, next) => {
            // Bypass this route in case we already have a signature that didn't expire
            if (store.state.questionnaire.consentDate && !store.getters.hasSignatureExpired) {
              return next({
                name: 'overview',
              });
            }
            next();
          }
        },
        {
          path: ':partId/address',
          name: 'address',
          component: () => import('../views/QuestAddress/QuestAddress.vue'),
          meta: {
            onlyWhenDossierOpened: true,
          },
        },
        {
          path: ':partId/insurance',
          name: 'insurance',
          component: () => import('../views/QuestInsurance/QuestInsurance.vue'),
          meta: {
            onlyWhenDossierOpened: true,
          },
        },
        {
          path: ':partId/doctor',
          name: 'doctor',
          component: () => import('../views/QuestDoctor/QuestDoctor.vue'),
          meta: {
            onlyWhenDossierOpened: true,
          },
        },
        {
          path: ':partId/questions',
          name: 'questions',
          component: () => import('../views/QuestQuestions/QuestQuestions.vue'),
          meta: {
            onlyWhenDossierOpened: true,
          },
        },
        {
          path: ':partId/recap',
          name: 'recap',
          component: () => import('../views/QuestRecap/QuestRecap.vue'),
          meta: {
            onlyWhenDossierOpened: true,
          },
        },
        {
          path: 'confirmation',
          name: 'confirmation',
          component: () => import('../views/Confirmation/Confirmation.vue'),
          meta: {
            onlyWhenDossierOpened: true,
          },
        },
        {
          path: 'passwordModification',
          name: 'password-modification',
          component: () => import('../views/PasswordModification/PasswordModification.vue'),
        },
        {
          path: 'admin/translation',
          name: 'translation',
          component: () => import('../views/AdminTranslation/AdminTranslation.vue'),
          meta: {
            public: true,  // Allow access to even if not logged in
            onlyWhenLoggedOut: true,
          },
        },
      ],
  },

  {
    // Default behaviour if no screening is set in the URL
    path: '/',
    name: 'default',
    redirect: () => {
      const su = LocalStoreService.localItemKeys.screeningUnit;
      const redirectTo = LocalStoreService.itemExists(su) ?
        `${getScreeningUnitNameFromID(LocalStoreService.getItem(su))}/login` :
        `${process.env.VUE_APP_DEFAULT_ROUTE_PREFIX}/login`;
      return redirectTo;
    },
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
}
});

router.beforeEach((to, from, next) => {

  // If we are trying to go to a page where we already are, just ignore it.
  if (from.name === to.name) {
    return;
  }

  const isPublic = to.matched.some((record) => record.meta.public);
  const isAtHome = to.matched.some((record) => record.meta.atHome);
  const onlyWhenLoggedOut = to.matched.some((record) => record.meta.onlyWhenLoggedOut);
  const loggedIn = TokenService.tokenExists() && (store.state.isAuthenticated || store.state.isIdentified);
  const suName = to.params.screening;
  const onlyWhenDossierOpened = to.matched.some((record) => record.meta.onlyWhenDossierOpened)

  // Prevent the user to change pages without having the latest app version
  if (store.state.isAppLoaded) {
    if (!store.state.appVersion.isLatest) {
      window.location.reload();
    } else {
      // If it's the latest version, check also the last time we checked it
      const a = moment(store.state.appVersion.lastCheckDate);
      const b = moment();
      if (b.diff(a, 'days') > 0) {
        window.location.reload();
      }
    }
  }

  if (suName !== undefined) {
    // Redirect to homepage if it's not a supported SU
    if (!SUPPORTED_SU.includes(suName.toLocaleLowerCase())) {
      return next({
        name: 'default',
      });
    }
  }
  
  // If authentified when can't access to atHome routes so redirect to home
  if (store.state.isAuthenticated && isAtHome) {
    return next({
      path: '/',
    });
  }

  // If authentified when can't access to non-atHome routes so redirect to atHomeIdentity
  if (store.state.isIdentified && !isAtHome) {
    return next({
      name: 'atHomeIdentity',
    });
  }

  if (!loggedIn && !isPublic && !isAtHome) {
    return next({
      path: '/',
    });
  }

  if (!loggedIn && !isPublic && isAtHome) {
    return next({
      name: 'atHomeIdentification',
    });
  }

  // Do not allow user to visit login page or register page if they are logged in
  // Pro users should access to the login page in order to open the participant dossier
  if (loggedIn && onlyWhenLoggedOut && !store.state.isAuthenticated) {
    return next({
      name: 'home',
    });
  }

  // Do not allow to come back after confirmation
  // Go back to login page
  if (from.name === 'confirmation' && !!to.name && !isAtHome) {
    if (to.name !== 'participant') {
      return next({
        name: 'participant',
      });
    }
  }

  // Do not allow to come back after confirmation
  // Go back to login page
  if (from.name === 'confirmation' && !!to.name && isAtHome) {
    if (to.name !== 'atHomeIdentification') {
      return next({
        name: 'atHomeIdentification',
      });
    }
  }
  
  // If we are logged in but no dossier opened we must go/stay
  // on participant page to open a dossier
  if (loggedIn && onlyWhenDossierOpened && !store.state.isDossierOpened) {
    return next({
      name: 'participant',
    });
  }

  next();
});

export default router;
